import { List, Map, Set } from "immutable"
import * as Moment from "moment"
import * as React from "react"
import { errorText, Event, getActivityClusters, getDatesForPeriod, getEventFilter, getEvents, loadingText, Region } from "../../custom_api/api"
import { moment } from "../../custom_utils/moment"
import { emptyOpt, makeOpt } from "../../custom_utils/option"
import { ActivityCluster } from "../components/activity_cluster"
import { maybeTimeSpan } from "../components/activity_edition"
import { CtaBlock } from "../components/cta_block"
import { pushActivityFilter, pushActivitySearch, pushTag } from "../components/datalayer"
import { setFilterParam } from "../components/filters"
import { Jumbo } from "../components/jumbo"
import { Container } from "../components/library/container"
import { Link } from "../components/library/link"
import { ActivityClustersPageState, DateRangeOption, DateRanges, filterZeroState } from "../components/page_renderer"
import { QuickLink, QuickLinks } from "../components/quick_links"
import { PeriodSlug } from "../components/region_and_date_filter"
import { TeaserProps, Teasers, TeasersProps } from "../components/teaser"
import debounce = require("lodash.debounce")
import { slugify } from "../../custom_utils/helpers"
import { ApiResult, isLoaded } from "../../../Shared/api/apiTypes"

export interface CoreActivityCluster {
  readonly id: number
  readonly title: string
  readonly description: string
  readonly imageUrl: string
  readonly imageAlt: string
  readonly linkUrl: string
  readonly linkText?: string
}

export interface ActivityClustersPageProps {
  readonly kind: "activityClusters"
  readonly pageTitle: string
  readonly headerTitle: string
  readonly description: string
  readonly imageUrl: string
  readonly imageAlt: string
  readonly coreActivityClusters: List<CoreActivityCluster>
  readonly coreActivityAmount:number
  readonly regions: ApiResult<Map<string, Region>>
  readonly selectedPeriod: PeriodSlug
  readonly selectedRegion: string
  readonly selectedCluster: string
  readonly selectPeriod: (e: React.ChangeEvent<HTMLSelectElement>) => void
  readonly selectRegion: (e: React.ChangeEvent<HTMLSelectElement>) => void
  readonly toggleArchive: (cb?: () => void) => void
  readonly showArchive: boolean
  readonly ctaToggle: boolean
  readonly ctaTitle: string
  readonly ctaDescription: string
  readonly ctaPrimaryActionTitle: string
  readonly ctaPrimaryActionUrl: string
  readonly ctaSecondaryActionTitle: string
  readonly ctaSecondaryActionUrl: string
  readonly activityPageState: ActivityClustersPageState
  readonly setActivityState: (newState: Partial<ActivityClustersPageState>, cb?: () => void) => void
  readonly pageLanguage: string
}

export interface RegionInfo { name: string, slug: string }

const smallViewPortThreshold = 1024

const coreClusterToTeaser = (c: CoreActivityCluster): TeaserProps => ({
  headingLevel: 'h2',
  title: c.title,
  intro: c.description,
  imageAlt: c.imageAlt,
  imageUrl: c.imageUrl,
  linkUrl: c.linkUrl,
  linkText: c.linkText
})

const ranges: DateRanges = {
  'vandaag': { from: new Date(), until: new Date() },
  'morgen': { from: moment().add(1, 'days').toDate(), until: moment().add(1, 'days').toDate() },
  'komende-zeven-dagen': { from: new Date(), until: moment().add(7, 'days').toDate() },
  'komende-dertig-dagen': { from: new Date(), until: moment().add(30, 'days').toDate() },
  'any': { from: new Date(), until: new Date(9999, 11) }
}

export class ActivityClustersPage extends React.Component<ActivityClustersPageProps> {
  constructor(props: ActivityClustersPageProps) {
    super(props)

    this.updateResults = this.updateResults.bind(this)
    this.eventBlock = this.eventBlock.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.updateEvents = this.updateEvents.bind(this)
  }

  componentDidMount() {
    this.updateResults(this.props, () => this.scrollToCluster(this.props.selectedCluster))

    const { regions } = this.props

    // Fetch the filters, set the category filter, then fetch the Events
    getEventFilter().then(filter => {
      const selectedCategories = this.props.selectedCluster ? Set<number>(this.props.selectedCluster.split('-').map(x => Number(x))) : Set<number>()
      const selectedRegion = this.props.selectedRegion && isLoaded(regions) && regions.find(x => x.slug == this.props.selectedRegion)?.id || undefined
      const dateRangeOption: DateRangeOption = this.props.selectedPeriod
      const dates = ranges[dateRangeOption]
      this.props.setActivityState({ ...filter, ...dates, selectedCategories, dateRangeOption, selectedRegion }, this.updateEvents)
    })

    document.title = this.props.pageTitle

    window.addEventListener('resize', this.handleResize)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  handleResize() {
    this.props.setActivityState(
      ({ isSmallViewport: window.innerWidth < smallViewPortThreshold })
    )
  }

  updateEvents() {
    if (this.props.showArchive) return
    const { selectedRegion, selectedPage: page, selectedCategories, pageSize, from, until, search } = this.props.activityPageState
    getEvents({ page, pageSize, selectedRegion, selectedCategories, from, until, search })
      .then(data => {
        this.props.setActivityState({
          events: List(data.events),
          resultCount: data.count,
          categoryEventCount: data.categoryEventCount,
          regionEventCount: data.regionEventCount,
          anyRegionEventCount: data.anyRegionEventCount,
          datesEventCount: data.datesEventCount
        })
      })
  }

  toggleCategory(id: number) {
    const { categories, selectedCategories } = this.props.activityPageState

    // Report toggle to GA
    const category = categories.get(id)
    const coreCluster = this.props.coreActivityClusters.some(c => c.id == id) ? ' - Kern' : ''
    const newState = selectedCategories.has(id) ? ' - uit' : ' - aan'
    pushActivityFilter('categorie', category + coreCluster + newState)

    // Store categories in query params
    const newCategories = selectedCategories.has(id) ? selectedCategories.remove(id) : selectedCategories.add(id)
    setFilterParam('categorie', newCategories.toArray().sort().join('-')) // sorting to ensure the same combinations always look the same

    // Actually toggle category
    this.props.setActivityState({
      selectedPage: 1,
      selectedCategories: newCategories
    }, this.updateEvents)
  }

  updateSelectedRegion(selectedRegion: number) {
    const location = this.props.activityPageState.regions.get(selectedRegion)
    pushActivityFilter('waar', location ? location.name : 'Overal')
    setFilterParam('waar', selectedRegion ? location.slug : "")
    this.props.setActivityState({ selectedRegion, selectedPage: 1 }, this.updateEvents)
  }

  updateSelectedPage(selectedPage: number) {
    let element = document.getElementById("activities")
    element.scrollIntoView({ behavior: "smooth" })
    this.props.setActivityState({ selectedPage }, this.updateEvents)
  }

  updateDates(dateRangeOption: DateRangeOption) {
    const dates = ranges[dateRangeOption]
    pushActivityFilter('wanneer', dateRangeOption)
    setFilterParam('wanneer', dateRangeOption != "any" ? dateRangeOption : "")
    this.props.setActivityState({ ...dates, dateRangeOption, selectedPage: filterZeroState.selectedPage }, this.updateEvents)
  }

  debouncedUpdateEvents = debounce((search) => {
    if (search.trim()) pushActivitySearch(search)
    this.updateEvents()
  }, 500)

  updateSearch(search: string) {
    this.props.setActivityState(({ search, selectedPage: filterZeroState.selectedPage }), () => this.debouncedUpdateEvents(search))
  }

  resetFilter() {
    pushActivityFilter('all', 'reset')
    setFilterParam('waar', '')
    setFilterParam('wanneer', '')
    setFilterParam('categorie', '')
    this.props.setActivityState({
      ...filterZeroState,
      selectedCategories: Set<number>()
    },
      this.updateEvents
    )
  }

  toggleFilterVisibility() {
    pushActivityFilter('mobile-filter', this.props.activityPageState.expandFilter ? 'sluiten' : 'openen')
    this.props.setActivityState({ expandFilter: !this.props.activityPageState.expandFilter })
  }

  scrollToCluster(slug: string) {
    setTimeout(() => {
      const clusters = this.props.activityPageState.clusters
      if (clusters == "error" || clusters == "loading") return

      let cluster = clusters.filter(c => c.slug === slug)
      if (cluster.isEmpty()) return

      let element = document.querySelector(`[data-anchor="${slug}"]`)
      if (!element) return
      element.scrollIntoView({ behavior: "smooth" })
    }, 500)
  }

  componentWillReceiveProps(nextProps: ActivityClustersPageProps) {
    const p = this.props
    const np = nextProps

    const scrollToCluster = () => p.selectedCluster != np.selectedCluster ? this.scrollToCluster(np.selectedCluster) : undefined

    if (p.selectedPeriod == np.selectedPeriod && p.selectedRegion == np.selectedRegion) {
      scrollToCluster()
      return
    }

    this.updateResults(np, () => scrollToCluster())
  }

  toggleArchive() {
    window.scrollTo({ behavior: 'auto', top: 0 })
    this.props.toggleArchive(() => {
      this.updateResults(this.props)
      this.updateEvents()
    })
  }

  updateResults(props: ActivityClustersPageProps, cb?: () => void) {
    if (!props.showArchive) return
    this.props.setActivityState(
      { clusters: "loading", events: List<Event>() }),
      () => {
        getActivityClusters({
          cluster: emptyOpt(),
          edition: emptyOpt(),
          region: makeOpt(q =>
            props.selectedRegion
              ? q.Where(r => r.Slug.Equals(props.selectedRegion))
              : q
          ),
          activity: makeOpt(q => q.Where(a =>
            a.HideInOverview.Equals(false)
          )),
          location: emptyOpt(),
          event: makeOpt(
            q => this.props.showArchive
              ? q.Where(e => e.Date.IsBefore(Moment().toDate()))
              : q.Where(e => {
                let period = getDatesForPeriod(props.selectedPeriod)
                return e.Date.IsAfter(period.start.toDate())
                  .And(e.Date.IsBefore(period.end.toDate()))
              })
          )
        }).then(x => {
          if (x != "error" && x != "loading") {
            this.props.setActivityState(
              ({ clusters: x }),
              () => {
                if (cb) cb()
              }
            )
          }
        })
      }
  }

  render() {
    const p = this.props
    const s = this.props.activityPageState

    return (
      <div className="wide">
        <Jumbo
          headerTitle={p.headerTitle}
          contents={p.description}
          headerImageUrl={p.imageUrl}
          headerImageAlt={p.imageAlt}
          size="large"
          kind="activity"
        />

        {p.showArchive ? this.archive(p, s) : this.events(p, s)}

        <Container>
          <div className="grid__button">
            <a onClick={() => { this.toggleArchive() }}>
              {p.showArchive ? "Terug naar het actuele overzicht" : "Bekijk het archief"}
            </a>
          </div>
          <div className="grid__button">
            <Link className="" url="/terugblikken">Bekijk alle terugblikken</Link>
          </div>
        </Container>
      </div>
    )
  }

  events(p: ActivityClustersPageProps, s: ActivityClustersPageState) {
    const lastPage = Math.ceil(s.resultCount / s.pageSize)

    const maxPages = 9
    const pagesBracket = Math.floor(maxPages / 2)
    const active = (page) => 1 <= page && page <= lastPage
    const prevPage = s.selectedPage - 1
    const nextPage = s.selectedPage + 1
    let navArray: [number, string, boolean, string][] = [[prevPage, 'Vorige', active(prevPage), 'prev']]

    if (lastPage <= maxPages) {
      Array.from({ length: lastPage }, (_, i) => i + 1).forEach(i => navArray.push([i, i.toString(), active(i), i == lastPage ? 'last number' : 'number']))
    } else {
      // First link should never be below 1, and never over lastPage - maxPages
      const first = Math.min(Math.max(1, s.selectedPage - pagesBracket), lastPage - maxPages + 1)
      const last = Math.min(lastPage, s.selectedPage + pagesBracket)

      if (first > 1) navArray.push([1, '1', active(1), 'first number'])       // If the first page falls outside the maxPages range, add it explicitly
      if (first > 2) navArray.push([null, ellipses, false, 'ellipsis'])   // If the first link in the pager is 3 or higher, add ellipses

      Array.from({ length: maxPages }, (_, i) => first + i).forEach(i => navArray.push([i, i.toString(), active(i), i == lastPage ? 'last number' : 'number']))

      if (last < lastPage - 1) navArray.push([null, ellipses, false, 'ellipsis']) // Add ellipses if last page in range is more than 1 removed from actual last page
      if (last < lastPage) navArray.push([lastPage, lastPage.toString(), active(lastPage), 'last number']) // Add last page if not in range
    }

    navArray.push([nextPage, 'Volgende', active(nextPage), 'next'])

    if (s.isSmallViewport) {
      navArray = [
        [prevPage, 'Vorige', active(prevPage), 'prev'],
        [s.selectedPage, s.selectedPage.toString(), active(s.selectedPage), 'number'],
        [lastPage, lastPage.toString(), active(lastPage), 'last number'],
        [nextPage, 'Volgende', active(nextPage), 'next']
      ]
    }

    const coreClusters: TeasersProps = {
      items: p.coreActivityClusters.map(coreClusterToTeaser).toList(),
    }

    let eventBlocks: JSX.Element[] = s.events.map(this.eventBlock).toArray()

    // We only show the CTA on the first page
    if (p.ctaToggle && s.selectedPage == 1) {
      const ctaBlock = <li key={'ctablock-in-a-list'}><CtaBlock
        kind="cta_activity_block"
        key="cta_activity_block"
        title={p.ctaTitle}
        description={p.ctaDescription}
        primaryActionTitle={p.ctaPrimaryActionTitle}
        primaryActionUrl={p.ctaPrimaryActionUrl}
        secondaryActionTitle={p.ctaSecondaryActionTitle}
        secondaryActionUrl={p.ctaSecondaryActionUrl}
      /></li>

      // On 1 or 2 results
      s.events.count() < 3
        // stick the CTA at the end
        ? eventBlocks.push(ctaBlock)
        // else stick it in the middle
        : eventBlocks.splice(Math.ceil(s.events.count() / 2), 0, ctaBlock)
    }

    // Set up display for result count
    const results = s.resultCount == 1 ? 'resultaat' : 'resultaten'
    let count = `${s.resultCount} ${results}`

    // Append region if selected
    const selectedRegionName = s.regions.get(s.selectedRegion)?.name
    if (selectedRegionName) count += ' in ' + selectedRegionName

    return <>
      {
        coreClusters && coreClusters.items.count() > 0 && 
          <Container>
            <div className="block block--content-grid-cluster">
              <div className="block__inner container-fluid">
                <Teasers {...coreClusters} row_count={p.coreActivityAmount} />
              </div>
            </div>
          </Container>
      }
      
      <Container>
        <section id="activities" className="activities">
          <div className="activities__container">
            {
              s.isSmallViewport && <button
                className="a-filter__button button black"
                aria-controls="filter-content"
                aria-expanded={s.expandFilter}
                onClick={() => { this.toggleFilterVisibility() }}
              >
                {s.expandFilter ? 'Sluit filter' : 'Open filter'}
              </button>
            }

            <div id="filter-content" className="activities__filter a-filter" aria-hidden={s.isSmallViewport ? !s.expandFilter : undefined}>
              <div className="a-filter__header">
                <h3 className="a-filter__title title--ms">Filter op</h3>
                <button className="a-filter__reset" onClick={() => this.resetFilter()}>Wis alle filters</button>
              </div>

              <div className="a-filter__group">
                <div className="a-filter__search">
                  <label className="a-filter__group-title" htmlFor="search">Zoek (activiteit of onderwerp):</label>
                  <input className="form__input" id="search" type="text" placeholder="Ik zoek..." value={s.search} onChange={(e) => this.updateSearch(e.target.value)} autoComplete="on"/>
                </div>
              </div>

              {
                s.regions.isEmpty() ? <></> : <div className="a-filter__group">
                  <label className="a-filter__group-title" htmlFor="activity-loc">Waar?</label>
                  <div className="a-filter__select form__select">
                    <select
                      id="activity-loc"
                      onChange={(e) => {
                        e.preventDefault()
                        this.updateSelectedRegion(Number(e.target.value) || null)
                      }
                      } value={s.selectedRegion || ""}>
                      <option key="any-location" value="">Overal {s.anyRegionEventCount != null ? ` (${s.anyRegionEventCount})` : ""}</option>
                      {s.regions
                        .map((info, id) => <option key={'location-' + id} value={id}>{info.name} ({s.regionEventCount.get(id.toString()) || 0})</option>)
                        .toArray()}
                    </select>
                  </div>
                </div>
              }
              <div className="a-filter__content">
                {
                  s.categories.isEmpty() ? <></> : <div className="a-filter__group">
                    <p className="a-filter__group-title">Onderwerpen</p>
                    {s.categories.map((category, id) =>
                      <div key={'category-' + id} className="a-filter__check form__check">
                        <input 
                          id={`cat-${id}`} 
                          type="checkbox" 
                          checked={s.selectedCategories.includes(id)} 
                          name={category}
                          value={category}
                          onChange={() => this.toggleCategory(id)}
                          aria-describedby={`cat-${slugify(category)}`}
                        />
                        <label id={`cat-${slugify(category)}`} htmlFor={`cat-${id}`}>{category} ({s.categoryEventCount.get(id.toString()) || 0})</label>
                      </div>).toArray()}
                  </div>
                }

                <div className="a-filter__group">
                  <p className="a-filter__group-title">Wanneer?</p>
                  <div className="a-filter__radio form__radio">
                    <input type="radio" name="when" id="any" value="any" checked={s.dateRangeOption == 'any'} onChange={() => this.updateDates('any')} />
                    <label htmlFor="any">Elk moment ({s.datesEventCount.any || 0})</label>
                  </div>
                  <div className="a-filter__radio form__radio">
                    <input type="radio" name="when" id="today" value="today" checked={s.dateRangeOption == 'vandaag'} onChange={() => this.updateDates('vandaag')} />
                    <label htmlFor="today">Vandaag ({s.datesEventCount.today || 0})</label>
                  </div>
                  <div className="a-filter__radio form__radio">
                    <input type="radio" name="when" id="tomorrow" value="tomorrow" checked={s.dateRangeOption == 'morgen'} onChange={() => this.updateDates('morgen')} />
                    <label htmlFor="tomorrow">Morgen ({s.datesEventCount.tomorrow || 0})</label>
                  </div>
                  <div className="a-filter__radio form__radio">
                    <input type="radio" name="when" id="7days" value="7days" checked={s.dateRangeOption == 'komende-zeven-dagen'} onChange={() => this.updateDates('komende-zeven-dagen')} />
                    <label htmlFor="7days">Komende 7 dagen ({s.datesEventCount.seven_days || 0})</label>
                  </div>
                  <div className="a-filter__radio form__radio">
                    <input type="radio" name="when" id="30days" value="30days" checked={s.dateRangeOption == 'komende-dertig-dagen'} onChange={() => this.updateDates('komende-dertig-dagen')} />
                    <label htmlFor="30days">Komende 30 dagen ({ s.datesEventCount.thirty_days || 0})</label>
                  </div>
                </div>
              </div>
            </div>

            <div className="activities__content a-content">
              <div className="a-filter__header">
                <h3 className="a-filter__title title--ms">{count}</h3>
              </div>

              <ul className="a-content__list">
                {eventBlocks}
              </ul>

              {lastPage > 1 && <ul className="a-pager">{navArray.map(([page, label, active, extraClasses], i) =>
                active
                  ? <li key={'page-' + label + extraClasses} className={`${page == s.selectedPage ? 'current' : ''} ${extraClasses ? extraClasses : ''}`} aria-current={page == s.selectedPage ? 'true' : undefined}><a className={!s.isSmallViewport && page == s.selectedPage ? 'weight--bold' : ''} onClick={(e) => { e.preventDefault(); this.updateSelectedPage(page) }}>{label} </a></li>
                  : <li key={'page-' + label + i} className={`${page == s.selectedPage ? 'current' : ''} ${extraClasses ? extraClasses : ''}`} aria-current={page == s.selectedPage ? 'true' : undefined}><span className={!s.isSmallViewport && page == s.selectedPage ? 'weight--bold' : ''}>{label} </span></li>
              )}</ul>}

            </div>

          </div>
        </section>
      </Container>
    </>
  }

  eventBlock(e: Event) {
    const activity = this.props.activityPageState.events.find(x => x.activityId == e.activityId)?.drupal_activity
    const date = e.date
    const url = activity ? this.decorateLink(activity.url) : e.signUpUrl

    return <li className="a-item" key={e.eventId}>
      <Link className="a-item__link" url={url}>
        <div className="a-item__content">
          <div className="a-item__text">
            <h4 className="a-item__title title--ms"><span>{activity ? activity.header_title : e.activityTitle}</span></h4>
            {activity?.description &&
              <div className="a-item__intro" dangerouslySetInnerHTML={{__html: activity.description}} />
            }
          </div>

          <div className="a-item__meta">
            <span className="sr-text">Waar en wanneer: </span>
            <time dateTime={moment(e.date).format('YYYY-MM-DD')}>
              {moment(e.date).format('dd')} {date.toLocaleDateString('nl')} {maybeTimeSpan(e)}
              <svg className="edition__icon" width="15" height="17" aria-hidden="true"><use xlinkHref="#icon--calendar" /></svg>
            </time>
            <address>
              {e.locationName}
              <svg className="edition__icon" width="15" height="18" aria-hidden="true"><use xlinkHref="#icon--location" /></svg>
            </address>
          </div>
          
          {url && <div className="a-item__fauxlink">
            <svg className="a-item__fauxlink-icon" width="8px" height="14px" aria-hidden="true"><use xlinkHref="#icon--chevron-right"></use></svg>
            <span>Bekijk activiteit</span>
          </div>}
        </div>

        <div className="a-item__visual">
          {activity?.image_url ? (
            <img src={activity.image_url} alt={activity.image_alt} width="225" height="150" className="a-item__image" />
          ) : (
            <div className="a-item__placeholder"></div>
          )}
        </div>
      </Link>
    </li>
  }

  archive(p: ActivityClustersPageProps, s: ActivityClustersPageState) {
    return <>
      <Container>
        <h2>Activiteiten archief</h2>
      </Container>
      <Container extraProps={{ 'aria-live': 'polite' }}>
        {s.clusters == "loading" ? (
          <p>{loadingText}</p>
        ) : s.clusters == "error" ? (
          <p>{errorText}</p>
        ) : (
          !s.clusters.isEmpty() && (
            <>
              <QuickLinks
                title="Direct naar..."
                links={s.clusters.map<QuickLink>((cluster) => ({ title: cluster.title, url: cluster.slug })).toList()}
                handleScroll={(slug: string) => { setFilterParam('categorie', slug); this.scrollToCluster(slug) }}
                pushAnalyticsEvent={(label: string) => pushTag(label)}
              />

              {s.clusters.map((x, k) => <ActivityCluster {...x} row_count={p.coreActivityAmount} key={`activity-cluster-${k}`} />).toArray()}
            </>
          )
        )}
      </Container>
    </>
  }

  decorateLink(url: string): string {
    const { regions, selectedRegion, dateRangeOption } = this.props.activityPageState
    const params: string[] = []
    let result = url;

    if (selectedRegion != filterZeroState.selectedRegion) {
      params.push('waar=' + regions.get(selectedRegion)?.slug)
    }

    if (dateRangeOption != filterZeroState.dateRangeOption) {
      params.push('wanneer=' + dateRangeOption)
    }

    if (params.length > 0) {
      result = result + '?' + params.join('&')
    }

    return result
  }
}

const ellipses = '\u2026'